// The booking link's promo code (?promo=), checked as soon as a ride is held,
// so the visit shows what the checkout will take off before the guest gets
// there: the booking bar, the service's "In your visit" line and the drawer.
// It asks validate_voucher (read-only, promo codes only) exactly as the
// checkout's auto-apply does, against the same first booking, on the same
// rule (shouldApplyLinkPromo); the checkout still applies the code itself and
// the server prices the payment. Display only.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useCart } from "./cart";
import { fetchPromoLinkTerms, previewCode } from "./booking";
import { computeCohostPairing } from "./cohost";
import { linkPromoFor, shouldApplyLinkPromo, type LinkPromoPreview, type PromoLinkTerms } from "./link-promo";
import { checkoutLineCents, holdCoversWholePrice, isChideskItem } from "../components/checkout/useCheckoutSubmit";

/**
 * The link's terms for a ride still being picked, or null where the checkout
 * would not take the code off it. minSpend: whether that ride is judged on
 * the code's minimum spend (it will be the visit's first booking).
 */
export type PickedLinkPromo = { terms: PromoLinkTerms; minSpend: boolean };
type PreviewContext = { preview: LinkPromoPreview | null; recheck: () => void; picked: PickedLinkPromo | null };
const LinkPromoPreviewContext = createContext<PreviewContext>({ preview: null, recheck: () => {}, picked: null });

export function LinkPromoPreviewProvider({
  tenant,
  children,
}: {
  tenant: { id: string; slug: string };
  children: ReactNode;
}) {
  const { items } = useCart();
  const code = linkPromoFor(tenant.slug);
  const check = useMemo(() => {
    if (!code) return null;
    const lines = items.filter((item) => !isChideskItem(item));
    const pairing = computeCohostPairing(items);
    const charged = lines
      .map((item) => ({ item, cents: checkoutLineCents(item, pairing.siblingModes) }))
      .filter((line) => line.cents > 0);
    const chargedNowCents = charged.reduce((n, line) => n + line.cents, 0);
    // The checkout's own "Full payment" rule (its buckets): a ticket, a
    // full-payment booking, or a hold that costs its whole price.
    const fullPrice = charged.every(
      ({ item, cents }) =>
        item.metadata?.source === "event" ||
        item.metadata?.requires_full_payment === true ||
        holdCoversWholePrice(item, cents, (item.metadata?.addons_cents as number | undefined) ?? 0),
    );
    const serviceIds = [...new Set(charged.map(({ item }) => item.serviceId))];
    const first = lines.find((item) => typeof item.metadata?.booking_id === "string");
    const bookingId = (first?.metadata?.booking_id as string | undefined) ?? null;
    const applies = shouldApplyLinkPromo({
      code,
      codesAccepted: lines.length === items.length && bookingId !== null,
      chargedNowCents,
      alreadyTried: false,
      appliedCount: 0,
    });
    return applies && bookingId
      ? { key: `${code}|${bookingId}|${chargedNowCents}`, bookingId, chargedNowCents, fullPrice, serviceIds }
      : null;
  }, [code, items]);

  // The code's own terms (get_promo_link_terms), once per venue and code: a
  // ride the guest is still picking (nothing held, so nothing to check the
  // code against) shows its price after a percentage code.
  const [terms, setTerms] = useState<{ key: string; terms: PromoLinkTerms | null } | null>(null);
  const termsKey = code ? `${tenant.id}|${code}` : null;
  useEffect(() => {
    if (!termsKey || !code) return;
    let live = true;
    void fetchPromoLinkTerms(tenant.id, code).then((res) => {
      if (live) setTerms({ key: termsKey, terms: res.ok ? res : null });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsKey]);
  const currentTerms = terms && terms.key === termsKey ? terms.terms : null;

  const [answer, setAnswer] = useState<{ key: string; discountCents: number } | null>(null);
  // Asked again when the drawer opens (and so on the way back from a checkout
  // that refused the code): a code can expire or run out after the hold, and
  // what is shown must follow. The last answer stays until the new one.
  const [asks, setAsks] = useState(0);
  const recheck = useCallback(() => setAsks((n) => n + 1), []);
  useEffect(() => {
    if (!check || !code) return;
    let live = true;
    void previewCode({ tenantId: tenant.id, code, bookingId: check.bookingId, vouchersOnly: true }).then((res) => {
      if (live) setAnswer({ key: check.key, discountCents: res.ok ? res.discount_cents : 0 });
    });
    return () => {
      live = false;
    };
    // The key carries the code, the booking and the amount: one check per change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check?.key, tenant.id, asks]);

  // A held line whose service can defer its deposit: checkout may confirm
  // without a payment, and then no code is applied at all.
  const deferredHeld =
    !!currentTerms &&
    items.some((item) => (currentTerms.deferred_deposit_service_ids ?? []).includes(item.serviceId));
  const value = useMemo<LinkPromoPreview | null>(() => {
    if (deferredHeld) return null;
    if (!check || !code || !answer || answer.key !== check.key || answer.discountCents <= 0) return null;
    return {
      code,
      discountCents: Math.min(answer.discountCents, check.chargedNowCents),
      chargedNowCents: check.chargedNowCents,
      fullPrice: check.fullPrice,
      serviceIds: check.serviceIds,
    };
  }, [check, code, answer, deferredHeld]);

  // A ride still being picked takes the code only where the checkout would:
  // never beside a spa treatment (the checkout takes no code then); with
  // nothing held it will be the first booking, judged on the code itself
  // (minimum spend included); with a held visit, only when the code already
  // took something off the visit's first booking (the checkout judges the
  // whole code on that one), and the minimum spend was judged there.
  const picked = useMemo<PickedLinkPromo | null>(() => {
    if (!currentTerms || items.some(isChideskItem)) return null;
    if (items.length === 0) return { terms: currentTerms, minSpend: true };
    return value && value.discountCents > 0 ? { terms: currentTerms, minSpend: false } : null;
  }, [currentTerms, items, value]);

  const context = useMemo(() => ({ preview: value, recheck, picked }), [value, recheck, picked]);
  return <LinkPromoPreviewContext.Provider value={context}>{children}</LinkPromoPreviewContext.Provider>;
}

/** What the link's code will take off at checkout, once checked (null: none, or not known yet). */
export function useLinkPromoPreview(): LinkPromoPreview | null {
  return useContext(LinkPromoPreviewContext).preview;
}

/** The link's code for a ride still being picked (null: none, or the checkout would not take it off). */
export function useLinkPromoPicked(): PickedLinkPromo | null {
  return useContext(LinkPromoPreviewContext).picked;
}

/** Ask about the link's code again (the drawer opening). */
export function useLinkPromoRecheck(): () => void {
  return useContext(LinkPromoPreviewContext).recheck;
}
